// Punkt wejścia aplikacji, etap 5 planu
// (~/.claude/plans/tak-a-r-b-plan-humble-crane.md, sekcja "Etapy", punkt 5):
// trwałość i wejście ręczne. Lista utworów pochodzi teraz z IndexedDB
// (`store/db.ts`), nie z `import.meta.glob` wprost tutaj: glob zamieszkał
// wyłącznie w `store/zasiew.ts`, jedynym miejscu, które zasiewa pustą bazę
// korpusem z paczki.
//
// Gdy baza jest niedostępna (brak IndexedDB, błąd otwarcia, tryb prywatny),
// aplikacja MA DZIAŁAĆ, w trybie tylko do odczytu, na tym samym korpusie
// wbudowanym - cichy pusty ekran jest tu najgorszym możliwym zachowaniem,
// więc ten stan jest zgłaszany jednym zdaniem w stopce, nie ukrywany.

import './render/shell.css';
import { compile, compileVariants } from './chart/compile.ts';
import { dopasuj } from './render/dopasuj.ts';
import { podswietlSekcje } from './render/chart.ts';
import { renderujPelnyTekst } from './render/pelnyTekst.ts';
import { ODSTEP_KOLUMN_PX, przewiniecieDoKolumny, ulozKolumny } from './render/kolumny.ts';
import { transponujDoWyswietlenia } from './render/transpozycja.ts';
import { nastepna, poprzednia } from './pozycja.ts';
import { sledzWysokosc, ustawWysokosc } from './render/viewport.ts';
import { zarejestrujWorkera, type StanPwa } from './pwa/rejestracja.ts';
import { sprawdzTrwalosc, zdanieOTrwalosci } from './pwa/trwaloscDanych.ts';
import {
  BladMagazynu,
  listaSetlist,
  listaUtworow,
  usunSetliste,
  usunUtwor,
  zapiszSetliste,
  zapiszUtwor,
  zapiszUtworyRazem,
  type Setlista,
  type Utwor,
} from './store/db.ts';
import { KORPUS_WBUDOWANY, poprawkiMetadanych, zasiejJesliPusto } from './store/zasiew.ts';
import { dolaczZPliku, eksportujDoPliku, importujZPliku } from './store/plik.ts';
import { mountujWklejanie } from './ui/wklejanie.ts';
import { podepnijSterowanie } from './ui/sterowanie.ts';
import { formatujStanStroju, otworzPanelStroju } from './ui/stroj.ts';
import { otworzPanelChwytow } from './ui/chwyty.ts';
import { otworzStroik } from './ui/stroik.ts';
import { dodajGotowe, otworzArkusz } from './ui/arkusz.ts';
import { unikalneAkordy } from './music/chwyty.ts';
import { mountujEkranSetlisty } from './ui/setlista.ts';
import { filtrujUtwory, zbierzTagi } from './ui/filtrowanie.ts';
import { normalizeChordToken } from './chart/progression.ts';
import { startSluchanie, type StanSluchania, type StopFn } from './audio/mikrofon.ts';
import { odlicz, startMetronom } from './audio/metronom.ts';
import { createTracker, type Tracker } from './audio/section-tracker.ts';
import { buildLyricsLines, type LyricsLine } from './audio/text-matcher.ts';
import type { SttConfig } from './audio/stt.ts';
import type { ChartVariant } from './chart/types.ts';

function sortujUtwory(lista: Utwor[]): Utwor[] {
  return [...lista].sort((a, b) => a.tytul.localeCompare(b.tytul, 'pl'));
}

// -------------------------------------------------------------- setlisty (etap 8)

const KLUCZ_WYBRANEJ_SETLISTY = 'ognisko:wybrana-setlista';

/** Odczyt preferencji z `localStorage`, w `try`: utrata tej JEDNEJ wartości
 * (tryb prywatny, limit miejsca) ma znaczyć "wróć do wszystkie", nigdy
 * wywrócić start aplikacji - to preferencja widoku, nie dane śpiewnika. */
function wczytajWybranaSetliste(): string | null {
  try {
    return localStorage.getItem(KLUCZ_WYBRANEJ_SETLISTY);
  } catch {
    return null;
  }
}

/** Klucz ElevenLabs (Faza 3), wklejony recznie przez przycisk "klucz API" w
 * pasku akcji menu, przetrwa w `localStorage` - nigdy w kodzie ani repo. */
const KLUCZ_ELEVENLABS = 'ognisko-elevenlabs-key';
/** Whisper na GPU tailnetu, adres na stale (Faza 3, spec-stt-faza3.md) - to
 * nie jest sekret, wiec siedzi wprost w kodzie, w odroznieniu od klucza ElevenLabs. */
// Bramka tożsamości Kokpitu dla Whispera (tylko urządzenia właściciela).
// Do 25.09.2026 stał tu port :10008, czyli OCR: każde `/align` dostawało 404
// i śledzenie tekstu nigdy nie zadziałało, choć nic tego nie pokazywało.
const WHISPER_URL = 'https://serwer-hww-1.tail840397.ts.net:10010';

/** Konfiguracja warstw STT (Faza 3) do przekazania `startSluchanie()`. Czytana
 * przy kazdym wlaczeniu mikrofonu, nie raz przy starcie - zeby wklejenie
 * klucza w trakcie sesji zadzialalo od nastepnego wlaczenia, bez przeladowania. */
function getSttConfig(): SttConfig {
  let elevenLabsKey: string | null = null;
  try {
    elevenLabsKey = localStorage.getItem(KLUCZ_ELEVENLABS);
  } catch {
    // Brak localStorage (tryb prywatny, limit) - STT po prostu zostaje bez
    // warstwy ElevenLabs, Whisper i chromagram dzialaja dalej.
  }
  return { elevenLabsKey, whisperUrl: WHISPER_URL };
}

function zapiszWybranaSetliste(id: string | null): void {
  try {
    if (id === null) localStorage.removeItem(KLUCZ_WYBRANEJ_SETLISTY);
    else localStorage.setItem(KLUCZ_WYBRANEJ_SETLISTY, id);
  } catch {
    // Preferencja po prostu nie przeżyje przeładowania - nic gorszego się nie dzieje.
  }
}

/** Liczy efektywną `UTWORY` z aktualnego `WSZYSTKIE_UTWORY`/`SETLISTY`/
 * `wybranaSetlistaId`. Gdy zapamiętana setlista już nie istnieje (skasowana
 * w międzyczasie, np. na innym urządzeniu przez import), wraca po cichu do
 * "wszystkie" - bez komunikatu o błędzie, tak jak mówi specyfikacja. */
function obliczWidoczneUtwory(): Utwor[] {
  if (wybranaSetlistaId === null) return sortujUtwory(WSZYSTKIE_UTWORY);

  const setlista = SETLISTY.find((s) => s.id === wybranaSetlistaId);
  if (!setlista) {
    wybranaSetlistaId = null;
    zapiszWybranaSetliste(null);
    return sortujUtwory(WSZYSTKIE_UTWORY);
  }

  // W KOLEJNOŚCI SETLISTY, nie alfabetycznie - to jest cały sens tej funkcji.
  // Pozycje wskazujące na utwór, którego już nie ma, są POMIJANE tutaj (przy
  // graniu i w menu), nigdy skasowane z zapisanej setlisty - to rozstrzyga
  // dopiero ekran edycji (`ui/setlista.ts`), pokazując je jako brakujące.
  const mapa = new Map(WSZYSTKIE_UTWORY.map((u) => [u.id, u] as const));
  return setlista.utwory.map((id) => mapa.get(id)).filter((u): u is Utwor => u !== undefined);
}

/** Widok korpusu wbudowanego w kształcie `Utwor`, dla trybu tylko do odczytu:
 * pola, których w tym trybie nikt nie czyta (`dodanyO`, `zmienionyO`), są
 * uzupełnione wartościami neutralnymi, żeby nie mnożyć osobnego typu. */
function utworZKorpusu(u: (typeof KORPUS_WBUDOWANY)[number]): Utwor {
  return {
    id: u.id,
    tytul: u.tytul,
    wykonawca: u.wykonawca,
    zrodlo: 'wbudowany',
    rawTab: u.rawTab,
    dodanyO: '',
    zmienionyO: null,
  };
}

/** Wszystkie utwory z bazy, NIEFILTROWANE - jedyne miejsce, z którego ekran
 * setlisty (`ui/setlista.ts`) bierze sekcję "dodaj". */
let WSZYSTKIE_UTWORY: Utwor[] = [];
/** Efektywna lista widoczna w menu i grana brzegiem ekranu (etap 8): albo
 * `WSZYSTKIE_UTWORY` posortowane alfabetycznie ("wszystkie"), albo utwory
 * wybranej setlisty W JEJ KOLEJNOŚCI. To jest CAŁY sens setlist - `pokazMenu`
 * i `przesun` nie wiedzą nic o setlistach, czytają tylko tę tablicę. */
let UTWORY: Utwor[] = [];
let SETLISTY: Setlista[] = [];
/** `null` = "wszystkie". Preferencja widoku, nie dane śpiewnika - stąd
 * `localStorage`, nie osobny magazyn IndexedDB (patrz specyfikacja etapu 8). */
let wybranaSetlistaId: string | null = null;

// -------------------------------------------------------- szukanie, tagi, ulubione

/** Tekst wpisany w polu szukania menu. Preferencja widoku (jak
 * `wybranaSetlistaId`), TYLKO w pamięci - w odróżnieniu od setlisty nie ma
 * sensu przetrwać przeładowania, bo to chwilowy filtr, nie wybór trwały. */
let filtrSzukaj = '';
/** Chip "ulubione" w rzędzie filtrów. */
let filtrTylkoUlubione = false;
/** Wybrany chip tagu, `null` = żaden (wszystkie tagi przechodzą). */
let filtrTag: string | null = null;

/** Filtruje `UTWORY` (już w kolejności setlisty albo alfabetycznej) przez
 * szukanie/ulubione/tag do WYŚWIETLENIA w menu. CELOWO osobna funkcja od
 * `obliczWidoczneUtwory`: `UTWORY` samo w sobie musi zostać nietknięte, bo
 * `indeks`/`przesun` (przewijanie brzegiem ekranu w widoku grania) liczą
 * pozycje względem NIEGO, nie względem tego, co akurat widać w menu po
 * wpisaniu szukanego tekstu. */
function obliczListeMenu(): Utwor[] {
  return filtrujUtwory(UTWORY, filtrSzukaj, filtrTylkoUlubione, filtrTag);
}
let trybTylkoDoOdczytu = false;
/** Powód trybu tylko do odczytu, po polsku, pokazywany w stopce menu. `null`
 * dopóki tryb tylko do odczytu się nie uruchomił. */
let powodTylkoDoOdczytu: string | null = null;

/** Podgląd techniczny (`?diag=1`): dopisuje poziom degradacji, rozmiar pisma
 * i listę tego, co degradacja ukryła. Domyślnie WYŁĄCZONY, bo to diagnostyka
 * dla mnie, a na ekranie grającego była tylko szumem w najdroższym miejscu. */
const DIAGNOSTYKA = new URLSearchParams(location.search).get('diag') === '1';

let indeks = 0;

/** Reczne sledzenie pozycji (Faza 1): `null` = podswietlenie wylaczone
 * (domyslnie), pierwsze dotkniecie brzegu ekranu aktywuje sekcje 0. Resetuje
 * sie do `null` przy kazdym `pokazUtwor()` (zmiana utworu). */
let aktywnaSekcja: number | null = null;

/** Poczatek gestu przeciagniecia (touchstart), do wykrycia swipe'u w widoku
 * grania. `null` gdy zaden dotyk nie trwa. */
let startX: number | null = null;

// ----------------------------------------------------- Faza 2: automat akordow

/** Czy sluchanie mikrofonu jest wlaczone (stan przycisku, przetrwa miedzy
 * renderami widoku grania - w odroznieniu od `aktywnaSekcja`, ktora resetuje
 * sie przy zmianie utworu). */
let sluchaAutomat = false;
/** Widok grania: skrot jednoekranowy (`false`, domyslnie) albo pelny tekst
 * przewijalny (`true`). Przetrwa miedzy utworami - `pokazUtwor()` CELOWO go
 * nie resetuje, bo wybor widoku jest preferencja grajacego, nie stanem
 * utworu (w odroznieniu od `aktywnaSekcja`, ktora resetuje sie zawsze). */
let trybPelnegoTekstu = false;
/** Funkcja zatrzymujaca aktualne sluchanie, `null` gdy nie sluchamy. */
let zatrzymajSluchanie: StopFn | null = null;

// -------------------------------------------------------------- metronom

const METRONOM_BPM_MIN = 40;
const METRONOM_BPM_MAX = 240;
const METRONOM_BPM_KROK = 5;
const METRONOM_BPM_DOMYSLNE = 100;

/** Czy metronom aktualnie tyka (stan przycisku `.og-metro`, ten sam wzorzec
 * co `sluchaAutomat` dla mikrofonu). */
let metronomAktywny = false;
/** Funkcja zatrzymujaca metronom, `null` gdy nie tyka. */
let zatrzymajMetronom: (() => void) | null = null;
/** BPM panelu metronomu. Domyslnie `Chart.meta.tempo` utworu (gdy podany w
 * tabie), w przeciwnym razie `METRONOM_BPM_DOMYSLNE` - patrz ustawienie w
 * `renderujTrescUtworu`, przy zmianie utworu (`ostatniUtworDlaMetronomu`). */
let metronomBpm = METRONOM_BPM_DOMYSLNE;
/** Id utworu, dla ktorego `metronomBpm` zostal ostatnio ustawiony domyslnie -
 * wlasny znacznik zamiast resetowania `metronomBpm` w `pokazUtwor()` wprost,
 * bo tempo zyje w `Chart.meta`, policzone dopiero w `renderujTrescUtworu`
 * (kompilacja), a przelaczenie trybu tekst/skrot wola te funkcje PONOWNIE dla
 * TEGO SAMEGO utworu i nie ma prawa nadpisac recznie ustawionego BPM. */
let ostatniUtworDlaMetronomu: string | null = null;

function przytnijBpm(bpm: number): number {
  return Math.max(METRONOM_BPM_MIN, Math.min(METRONOM_BPM_MAX, Math.round(bpm)));
}

/** Zatrzymuje metronom, jesli tyka - wywolywane przy zmianie utworu i
 * wyjsciu do menu (specyfikacja: "Song change or menu exit: auto-stop
 * metronome"), tym samym wzorcem co zatrzymanie mikrofonu w `pokazUtwor()`. */
function zatrzymajMetronomJesliAktywny(): void {
  if (zatrzymajMetronom) {
    zatrzymajMetronom();
    zatrzymajMetronom = null;
  }
  metronomAktywny = false;
}

/** Startuje metronom Web Audio z aktualnym `metronomBpm`, takt 4/4. */
function wystartujMetronom(): void {
  metronomAktywny = true;
  zatrzymajMetronom = startMetronom({ bpm: metronomBpm, beatsPerMeasure: 4 });
}
/** Tracker zbudowany z progresji AKTUALNIE WYSWIETLONEGO wariantu (po
 * transpozycji/capo - to, co gitarzysta naprawde gra). Przebudowywany przy
 * kazdym `renderujTrescUtworu`. */
let trackerAktualny: Tracker | null = null;
/** Ostatni akord podany trackerowi, do odfiltrowania powtorzen tego samego
 * akordu z kolejnych probek 200 ms - bufor trackera ma nosic ZMIANY akordu,
 * nie dziesiec razy ten sam akord z jednego trzymanego chwytu. */
let ostatniAkordDlaTrackera: string | null = null;
/** Faza 3: sekcje tekstu AKTUALNIE WYSWIETLONEGO utworu, do dopasowania
 * transkrypcji STT. Przebudowywane przy kazdym `renderujTrescUtworu`, tak jak
 * `trackerAktualny` - `przelaczSluchanie` czyta je dopiero przy wlaczeniu
 * mikrofonu, nie trzyma wlasnej kopii. */
let linieLyrics: LyricsLine[] = [];
/** Znacznik czasu (ms), do ktorego reczne dotkniecie brzegu ma pierwszenstwo
 * nad automatem - "reczne przelaczanie nadpisuje automat na 5 sekund". */
let recznaIngerencjaDo = 0;
const CZAS_NADPISANIA_MS = 5000;
let wakeLock: WakeLockSentinel | null = null;
/** Czy próba wzięcia blokady ekranu już padła, niezależnie od jej wyniku.
 * Osobno od `wakeLock`, bo po odmowie `wakeLock` zostaje `null` na zawsze i
 * sam w sobie nie odróżnia „jeszcze nie prosiłem" od „poproszono, odmówiono". */
let ekranProbowany = false;

/** Ostatni stan zgłoszony przez `zarejestrujWorkera`. Trzymany osobno od DOM,
 * bo stopka jest budowana od nowa przy każdym wejściu do menu
 * (`app.replaceChildren()`), a rejestracja worera żyje dłużej niż pojedynczy
 * widok i melduje asynchronicznie, czasem długo po pierwszym `pokazMenu()`. */
let stanPwa: StanPwa = { rodzaj: 'brak' };

/** Zdanie o trwałości danych, albo `null` gdy nie ma nic do powiedzenia.
 * Trzymane osobno od DOM z tego samego powodu co `stanPwa`: stopka powstaje od
 * nowa przy każdym wejściu do menu, a odpowiedź o trwały magazyn przychodzi
 * asynchronicznie i zwykle później niż pierwsze `pokazMenu()`. */
let zdanieTrwalosci: string | null = null;

function el<T extends HTMLElement>(selektor: string): T {
  const znaleziony = document.querySelector<T>(selektor);
  if (!znaleziony) throw new Error(`Brak elementu ${selektor} w DOM.`);
  return znaleziony;
}

// ---------------------------------------------------------------- widok gry

/**
 * Kompiluje, transponuje i dopasowuje `utwor` do wnętrza `tresc`, i podpina
 * panel stroju pod wiersz metadanych. Wydzielona z `pokazUtwor()`, bo panel
 * stroju musi umieć przeliczyć skrót PONOWNIE po każdej zmianie `+`/`-`, BEZ
 * wyburzania całego widoku gry (`app.replaceChildren()`) - inaczej nakładka
 * panelu (dołączona do `document.body`, patrz `ui/stroj.ts`) przeżyłaby
 * przeliczenie, ale `tresc`/`diag` muszą zostać TYMI SAMYMI elementami DOM
 * między wywołaniami tej funkcji, żeby przeliczenie nie zburzyło ich razem
 * z resztą widoku gry.
 */
function renderujTrescUtworu(utwor: Utwor, tresc: HTMLDivElement, diag: HTMLDivElement | null): void {
  const app = el<HTMLDivElement>('#app');
  const przesuniecie = (utwor.transpozycja ?? 0) - (utwor.capo ?? 0);

  if (trybPelnegoTekstu) {
    app.dataset.widok = 'granie-tekst';
    if (diag) diag.textContent = '';
    renderujPelnyTekst(tresc, utwor.rawTab, przesuniecie);
    szerokoscKolumny = ulozKolumny(app, tresc);
  } else {
    app.dataset.widok = 'granie';
    delete app.dataset.kolumny;
    szerokoscKolumny = null;
  }

  // Faza 3: sekcje tekstu do dopasowania STT, budowane niezaleznie od
  // `compile()` (inny, prostszy parser tego samego `rawTab`, patrz
  // `text-matcher.ts`) - blad tutaj (utwor niesparsowalny) NIE ma zablokowac
  // widoku gry, tylko wylaczyc dopasowanie sekcji przez STT.
  try {
    linieLyrics = buildLyricsLines(utwor.rawTab);
  } catch {
    linieLyrics = [];
  }

  const chart = compile(utwor.id, utwor.rawTab);

  // Domyslny BPM panelu metronomu: z `Chart.meta.tempo`, gdy autor taba go
  // podal, inaczej `METRONOM_BPM_DOMYSLNE`. TYLKO przy zmianie utworu -
  // przelaczenie trybu tekst/skrot wola te funkcje ponownie dla TEGO SAMEGO
  // utworu (`ostatniUtworDlaMetronomu` niezmienione) i nie ma prawa nadpisac
  // wartosci, ktora grajacy mogl juz zmienic w panelu.
  if (utwor.id !== ostatniUtworDlaMetronomu) {
    ostatniUtworDlaMetronomu = utwor.id;
    metronomBpm = przytnijBpm(chart.meta.tempo ?? METRONOM_BPM_DOMYSLNE);
  }

  if (!chart.usable) {
    if (!trybPelnegoTekstu) {
      const bledy = chart.diagnostics.filter((d) => d.severity === 'error').map((d) => d.message);
      tresc.className = 'zar-blad';
      tresc.textContent = bledy.join(' ');
    }
    return;
  }

  const warianty = compileVariants(chart).map((w) => transponujDoWyswietlenia(w, przesuniecie));
  const strojInfo = formatujStanStroju(utwor.transpozycja ?? 0, utwor.capo ?? 0);

  // Tracker budowany z pierwszego wariantu (po transpozycji i capo), potrzebny
  // w OBU trybach: w skrocie do podswietlania sekcji, w pelnym tekscie do
  // rozpoznawania sekcji i automatycznego przewijania do niej.
  trackerAktualny = zbudujTracker(warianty[0]!);
  ostatniAkordDlaTrackera = null;

  if (trybPelnegoTekstu) return;

  const { wynik, wariant, bezZawijania } = dopasuj(tresc, warianty, {
    showDroppedInfo: DIAGNOSTYKA,
    wierszMetaZawsze: true,
    strojInfo,
  });

  podswietlSekcje(tresc, aktywnaSekcja);

  if (diag) {
    diag.textContent = `${utwor.tytul} · poziom ${wynik.level} · ${wynik.fontSizePx}px · `
      + `${wynik.status} · ${bezZawijania ? 'jeden wiersz' : 'zawijanie na frazach'} · `
      + `${wynik.measurements} pomiarów`;
  }

  // Wiersz metadanych jest CELEM DOTKNIĘCIA panelu stroju, zawsze (patrz
  // `wierszMetaZawsze: true` wyżej - dzięki niemu element istnieje nawet dla
  // utworu bez żadnych metadanych). `stopPropagation()` jest tu warunkiem
  // działania, nie ostrożnością: bez niego kliknięcie dobija bąbelkowaniem do
  // obsługi ekranu gry (`el('#app').addEventListener('click', ...)` niżej),
  // trafia w środkową strefę i natychmiast wraca do menu - ten błąd był w tym
  // repozytorium już dwa razy.
  const wierszMeta = tresc.querySelector<HTMLElement>('.og-meta');
  wierszMeta?.addEventListener('click', (zdarzenie) => {
    zdarzenie.stopPropagation();
    otworzPanelStroju(document.body, {
      utwor,
      naZmiane: async (nowaTranspozycja, nowyCapo) => {
        utwor.transpozycja = nowaTranspozycja;
        utwor.capo = nowyCapo;
        try {
          await zapiszUtwor(utwor);
        } catch {
          // Tryb tylko do odczytu albo błąd zapisu: strój zmienia się i tak w
          // WIDOKU (poniższe `renderujTrescUtworu`), bo grający chce usłyszeć
          // efekt natychmiast - zniknięcie po przeładowaniu jest tu mniejszym
          // złem niż zablokowanie funkcji dla trybu tylko do odczytu.
        }
        // Ustawienie jest wymaganiem 2. specyfikacji: PEŁNY `dopasuj()`, nie
        // podmiana tekstu - zmiana pisowni akordów zmienia szerokość wiersza,
        // więc może zmienić wybór między przebiegiem ostrym a łagodnym.
        renderujTrescUtworu(utwor, tresc, diag);
      },
    });
  });
}

/** Akordy utworu w tej wysokości, w jakiej widzi je grający (po transpozycji
 * i kapodastrze), unikalne, w kolejności pierwszego wystąpienia. Liczone na
 * żądanie, przy otwarciu arkusza chwytów, a nie przy każdym renderze. */
function akordyNaEkranie(utwor: Utwor): string[] {
  const chart = compile(utwor.id, utwor.rawTab);
  if (!chart.usable) return [];
  const przesuniecie = (utwor.transpozycja ?? 0) - (utwor.capo ?? 0);
  const wariant = compileVariants(chart)[0];
  if (!wariant) return [];
  const naEkranie = transponujDoWyswietlenia(wariant, przesuniecie);
  return unikalneAkordy(naEkranie.sections.flatMap((sekcja) => sekcja.progression.raw));
}

/** Buduje tracker Fazy 2 z sekcji `wariant`, normalizujac kazdy token akordu
 * przez `normalizeChordToken` (ten sam parser co reszta aplikacji), zeby
 * pisownia z widoku ("Am", nie "A-", nie "am") pasowala do nazw zwracanych
 * przez `detectChord`. */
function zbudujTracker(wariant: ChartVariant): Tracker {
  const sekcje = wariant.sections.map((sekcja) => ({
    chords: sekcja.progression.raw.map((token) => normalizeChordToken(token).value),
  }));
  return createTracker(sekcje);
}

/** Kiedy śledzenie tekstu ostatnio wskazało wers (ms od epoki). */
let ostatniWersCzas = 0;
/** Faktyczna szerokość kolumny pełnego tekstu, `null` przy jednej kolumnie. */
let szerokoscKolumny: number | null = null;

/** W układzie kolumn przewija `#tresc` w bok tak, żeby kolumna z `cel` stała
 * po lewej. Zwraca `false`, gdy układu kolumn nie ma. */
function przewinDoKolumny(cel: HTMLElement): boolean {
  const tresc = document.getElementById('tresc');
  if (szerokoscKolumny === null || !tresc) return false;
  const kontener = tresc.querySelector<HTMLElement>('.pt-container');
  const lewo = kontener ? kontener.getBoundingClientRect().left + tresc.scrollLeft : tresc.getBoundingClientRect().left;
  const x = cel.getBoundingClientRect().left + tresc.scrollLeft - lewo;
  const cele = przewiniecieDoKolumny(x, szerokoscKolumny, tresc.scrollLeft);
  if (cele !== null) tresc.scrollTo({ left: cele, behavior: 'smooth' });
  return true;
}

/** Przewinięcie widoku pełnego tekstu o ekran w przód albo w tył (pedał,
 * klawiatura): w kolumnach o jedną kolumnę w bok, bez kolumn o 80% w dół. */
function przewinPelnyTekst(kierunek: -1 | 1): void {
  const app = el<HTMLDivElement>('#app');
  const tresc = document.getElementById('tresc');
  if (szerokoscKolumny !== null && tresc) {
    tresc.scrollBy({ left: kierunek * (szerokoscKolumny + ODSTEP_KOLUMN_PX), behavior: 'smooth' });
  } else {
    app.scrollBy({ top: kierunek * app.clientHeight * 0.8, behavior: 'smooth' });
  }
}

/**
 * Prowadzi widok pełnego tekstu za śpiewającym: podświetla wers, przygasza
 * wersy już zaśpiewane i przewija tak, żeby bieżący stał na wysokości około
 * jednej trzeciej ekranu, a pod nim było widać, co zaraz trzeba zaśpiewać.
 * Nie przewija, gdy wers już stoi w tym pasie: ciągłe drobne szarpnięcia
 * ekranu męczą bardziej niż lekko niedokładna pozycja.
 */
function prowadzDoWersu(lineIndex: number, postep = 0): void {
  const app = document.getElementById('app');
  const tresc = document.getElementById('tresc');
  if (!app || !tresc) return;
  const cel = tresc.querySelector<HTMLElement>(`.pt-grupa[data-line-index="${lineIndex}"]`);
  if (!cel) return;
  // Następny wers dostaje lekką zapowiedź, gdy bieżący jest prawie
  // dośpiewany: serwer potrzebuje dwóch słów nowego wersu, zanim go wskaże
  // (mediana 1,8 s w pomiarze 25.09.2026), a śpiewający musi wiedzieć,
  // dokąd iść, wcześniej. Zapowiedź nie przesuwa ekranu i nie udaje pewności.
  const grupy = [...tresc.querySelectorAll<HTMLElement>('.pt-grupa')];
  const iAkt = grupy.indexOf(cel);
  const nastepna = postep >= 0.6 ? grupy.slice(iAkt + 1).find((g) => g.querySelector('.pt-txt')) ?? null : null;
  grupy.forEach((grupa) => {
    const nr = Number(grupa.dataset.lineIndex);
    grupa.classList.toggle('pt-grupa--aktywna', nr === lineIndex);
    grupa.classList.toggle('pt-grupa--minione', nr < lineIndex);
    grupa.classList.toggle('pt-grupa--nastepna', grupa === nastepna);
  });
  if (przewinDoKolumny(cel)) return;
  const pole = app.getBoundingClientRect();
  const wers = cel.getBoundingClientRect();
  const wzgledna = wers.top - pole.top;
  if (wzgledna > app.clientHeight * 0.2 && wzgledna < app.clientHeight * 0.45) return;
  app.scrollTo({ top: app.scrollTop + wzgledna - app.clientHeight * 0.3, behavior: 'smooth' });
}

/** Napis w doku mówiący, czy aplikacja słyszy i co. `null` zdejmuje napis. */
function pokazStanSluchania(stan: StanSluchania | { rodzaj: 'bez-mikrofonu' } | null): void {
  const srodek = document.querySelector<HTMLElement>('.og-dok-srodek');
  if (!srodek) return;
  let napis = srodek.querySelector<HTMLElement>('.og-sluch');
  if (stan === null) { napis?.remove(); srodek.classList.remove('og-dok-srodek--sluch'); return; }
  if (!napis) {
    napis = document.createElement('div');
    napis.className = 'og-sluch';
    napis.setAttribute('role', 'status');
    srodek.appendChild(napis);
  }
  srodek.classList.add('og-dok-srodek--sluch');
  napis.dataset.stan = stan.rodzaj === 'slysze' ? (stan.trafiony ? 'trafiony' : 'slysze') : stan.rodzaj;
  napis.textContent =
    stan.rodzaj === 'slucham' ? 'słucham…'
    : stan.rodzaj === 'brak-serwera' ? 'brak połączenia z serwerem ⓘ'
    : stan.rodzaj === 'bez-mikrofonu' ? 'mikrofon niedostępny'
    : stan.tekst.trim() === '' ? 'nie rozpoznaję słów'
    : `„${stan.tekst.trim().split(/\s+/).slice(-5).join(' ')}”`;
  if (stan.rodzaj === 'brak-serwera') {
    const pigulka = napis;
    void stanSieciLokalnej().then((dostep) => {
      if (dostep === null || dostep === 'granted' || pigulka.dataset.stan !== 'brak-serwera') return;
      pigulka.textContent = 'przeglądarka blokuje sieć lokalną ⓘ';
      pigulka.dataset.powod = 'siec-lokalna';
    });
  } else {
    delete napis.dataset.powod;
  }
  napis.onclick = stan.rodzaj === 'brak-serwera'
    ? (z) => { z.stopPropagation(); pokazPomocSerwera(napis?.dataset.powod === 'siec-lokalna'); }
    : null;
}

/** Stan uprawnienia do sieci lokalnej w Chromium (od wersji 142 strona
 * publiczna, jak `pages.dev`, nie może bez zgody sięgać do adresów sieci
 * lokalnej, a tailnet `100.x` się do nich zalicza). Pomiar 25.09.2026 na
 * Chromium 151: bez zgody `fetch` do serwera Whisper pada z „Permission was
 * denied for this request to access the `local` address space”, ze zgodą
 * przechodzi. `null`, gdy przeglądarka takiego uprawnienia nie zna (Safari). */
async function stanSieciLokalnej(): Promise<PermissionState | null> {
  try {
    const st = await navigator.permissions.query({ name: 'local-network-access' as PermissionName });
    return st.state;
  } catch {
    return null;
  }
}

/** Ognisko podane przez serwer Whispera (ten sam adres co `/align`): tam
 * przeglądarka nie pyta o sieć lokalną, bo strona i serwer są w jednym źródle. */
const OGNISKO_Z_SERWERA = `${WHISPER_URL}/`;

function pokazPomocSerwera(sieciLokalna: boolean): void {
  const arkusz = otworzArkusz(sieciLokalna ? 'Przeglądarka blokuje serwer' : 'Brak połączenia z serwerem');
  const tekst = document.createElement('div');
  tekst.className = 'og-pomoc';
  const akapit = (tresc: string, klasa?: string): HTMLParagraphElement => {
    const p = document.createElement('p');
    p.textContent = tresc;
    if (klasa) p.className = klasa;
    tekst.appendChild(p);
    return p;
  };
  if (sieciLokalna) {
    akapit('Serwer rozpoznawania śpiewu stoi w sieci Tailscale. Ta przeglądarka nie pozwala stronie z internetu łączyć się z siecią lokalną bez osobnej zgody.');
    const link = akapit('Najprościej otworzyć Ognisko prosto z serwera, wtedy zgoda nie jest potrzebna: ');
    const a = document.createElement('a');
    a.href = OGNISKO_Z_SERWERA;
    a.textContent = OGNISKO_Z_SERWERA;
    link.appendChild(a);
    akapit('Utwory dodane tutaj trzeba tam przenieść eksportem i importem z menu „⋯”, bo każdy adres ma własny śpiewnik.', 'og-pomoc-drobne');
    akapit('Można też zezwolić tej stronie na dostęp do sieci lokalnej w ustawieniach witryny (ikona obok adresu), a potem wyłączyć i włączyć mikrofon.', 'og-pomoc-drobne');
  } else {
    akapit('Aplikacja wysyła śpiew do serwera przez Tailscale.');
    akapit('Sprawdź, czy Tailscale jest włączony na tym urządzeniu i zalogowany na Twoje konto, a potem wyłącz i włącz mikrofon w aplikacji.');
  }
  arkusz.karta.appendChild(tekst);
  dodajGotowe(arkusz);
}

/** Wlacza/wylacza sluchanie mikrofonu (Faza 2). Wywolywana z przycisku
 * `.og-mic` w widoku grania. Odmowa uprawnien albo brak API wraca do stanu
 * wylaczonego i mowi o tym krotko - cichy powrot wygladalby jak przycisk,
 * ktory nie zareagowal na dotkniecie. */
async function przelaczSluchanie(przycisk: HTMLButtonElement): Promise<void> {
  if (sluchaAutomat) {
    zatrzymajSluchanie?.();
    zatrzymajSluchanie = null;
    sluchaAutomat = false;
    przycisk.classList.remove('og-mic--aktywny');
    pokazStanSluchania(null);
    return;
  }

  sluchaAutomat = true;
  przycisk.classList.add('og-mic--aktywny');

  zatrzymajSluchanie = await startSluchanie({
    onChord: (akord) => {
      if (akord.name === ostatniAkordDlaTrackera) return;
      ostatniAkordDlaTrackera = akord.name;

      const stan = trackerAktualny?.push(akord.name) ?? null;
      if (!stan || stan.activeSection === null) return;
      if (Date.now() < recznaIngerencjaDo) return; // reczne dotkniecie ma pierwszenstwo (5 s)
      if (stan.activeSection === aktywnaSekcja) return;

      aktywnaSekcja = stan.activeSection;
      const tresc = document.getElementById('tresc');
      if (!tresc) return;

      if (trybPelnegoTekstu) {
        // Śledzenie po tekście jest dokładniejsze niż po akordach: gdy przed
        // chwilą wskazało wers, akordy nie mają prawa przewijać gdzie indziej.
        if (Date.now() - ostatniWersCzas < 10_000) return;
        const cel = tresc.querySelector<HTMLElement>(`.pt-lbl[data-section-index="${stan.activeSection}"]`);
        if (cel && !przewinDoKolumny(cel)) cel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        podswietlSekcje(tresc, aktywnaSekcja);
      }
    },
    onLine: (lineIndex, _zrodlo, postep) => {
      ostatniWersCzas = Date.now();
      if (Date.now() < recznaIngerencjaDo) return;
      if (trybPelnegoTekstu) prowadzDoWersu(lineIndex, postep);
    },
    onStan: pokazStanSluchania,
    onError: () => {
      sluchaAutomat = false;
      zatrzymajSluchanie = null;
      przycisk.classList.remove('og-mic--aktywny');
      przycisk.title = 'Mikrofon niedostepny albo dostep odmowiony.';
      pokazStanSluchania({ rodzaj: 'bez-mikrofonu' });
    },
    sttConfig: getSttConfig(),
    lyricsLines: linieLyrics,
  });
}

/**
 * Panel metronomu: BPM (`-`/`+`, krok 5, zakres 40-240), "odlicz" (jeden
 * takt, patrz `audio/metronom.ts`) i przelacznik start/stop. Nakladka na
 * `document.body`, TEN SAM wzorzec co panel stroju (`ui/stroj.ts`) - reuzywa
 * jego klasy CSS (`.zar-stroj-nakladka`/`.zar-stroj-karta`/`.zar-stroj-wiersz`
 * itd.), bo wyglada identycznie: wiersz `-`/wartosc/`+` i pasek akcji pod
 * spodem. W odroznieniu od stroju metronom nie zalezy od tresci utworu (nie
 * ma czego przeliczac po kazdej zmianie), wiec caly panel jest zbudowany tu,
 * bez osobnego modulu.
 *
 * `przyciskIkony` to przycisk `.og-metro` z widoku grania - panel aktualizuje
 * jego klase `og-metro--aktywny` po kazdym przelaczeniu start/stop, zeby
 * puls na przycisku odzwierciedlal stan NAWET po zamknieciu panelu.
 */
function otworzPanelMetronomu(przyciskIkony: HTMLButtonElement): void {
  document.body.querySelector('.zar-stroj-nakladka')?.remove();

  const naklad = document.createElement('div');
  naklad.className = 'zar-stroj-nakladka';

  const karta = document.createElement('div');
  karta.className = 'zar-stroj-karta';
  naklad.appendChild(karta);

  const naglowek = document.createElement('p');
  naglowek.className = 'zar-stroj-naglowek';
  naglowek.textContent = 'metronom';
  karta.appendChild(naglowek);

  const wiersz = document.createElement('div');
  wiersz.className = 'zar-stroj-wiersz';

  const lbl = document.createElement('span');
  lbl.className = 'zar-stroj-etykieta';
  lbl.textContent = 'tempo (BPM)';

  const minus = document.createElement('button');
  minus.type = 'button';
  minus.className = 'zar-stroj-przycisk';
  minus.textContent = '−';
  minus.setAttribute('aria-label', 'bpm: mniej');

  const wart = document.createElement('span');
  wart.className = 'zar-stroj-wartosc';

  const plus = document.createElement('button');
  plus.type = 'button';
  plus.className = 'zar-stroj-przycisk';
  plus.textContent = '+';
  plus.setAttribute('aria-label', 'bpm: wiecej');

  const odswiezBpm = (): void => {
    wart.textContent = String(metronomBpm);
    minus.disabled = metronomBpm <= METRONOM_BPM_MIN;
    plus.disabled = metronomBpm >= METRONOM_BPM_MAX;
  };

  // Zmiana BPM w trakcie tykania restartuje silnik na nowym tempie od razu -
  // grajacy chce uslyszec nowe tempo natychmiast, nie dopiero po `stop`+`start`.
  const zmienBpm = (delta: number): void => {
    metronomBpm = przytnijBpm(metronomBpm + delta);
    odswiezBpm();
    if (metronomAktywny) {
      zatrzymajMetronomJesliAktywny();
      wystartujMetronom();
    }
  };

  minus.addEventListener('click', (z) => { z.stopPropagation(); zmienBpm(-METRONOM_BPM_KROK); });
  plus.addEventListener('click', (z) => { z.stopPropagation(); zmienBpm(METRONOM_BPM_KROK); });

  wiersz.append(lbl, minus, wart, plus);
  karta.appendChild(wiersz);
  odswiezBpm();

  const akcje = document.createElement('div');
  akcje.className = 'zar-stroj-akcje';

  const odliczBtn = document.createElement('button');
  odliczBtn.type = 'button';
  odliczBtn.className = 'zar-przycisk zar-przycisk--wtorny';
  odliczBtn.textContent = 'odlicz';
  odliczBtn.addEventListener('click', (z) => {
    z.stopPropagation();
    if (odliczBtn.disabled) return;
    odliczBtn.disabled = true;
    void odlicz({ bpm: metronomBpm, beatsPerMeasure: 4 }).finally(() => {
      odliczBtn.disabled = false;
    });
  });

  const startStop = document.createElement('button');
  startStop.type = 'button';

  const odswiezStartStop = (): void => {
    startStop.className = `zar-przycisk ${metronomAktywny ? 'zar-przycisk--niebezpieczny' : 'zar-przycisk--glowny'}`;
    startStop.textContent = metronomAktywny ? 'stop' : 'start';
    przyciskIkony.classList.toggle('og-metro--aktywny', metronomAktywny);
  };
  odswiezStartStop();

  startStop.addEventListener('click', (z) => {
    z.stopPropagation();
    if (metronomAktywny) zatrzymajMetronomJesliAktywny();
    else wystartujMetronom();
    odswiezStartStop();
  });

  akcje.append(odliczBtn, startStop);
  karta.appendChild(akcje);

  // Zamkniecie dotknieciem poza panelem, ten sam wzorzec co panel stroju.
  naklad.addEventListener('click', () => naklad.remove());
  karta.addEventListener('click', (z) => z.stopPropagation());

  document.body.appendChild(naklad);
}

function pokazUtwor(): void {
  const utwor = UTWORY[indeks];
  if (!utwor) return;

  aktywnaSekcja = null;
  // Słuchanie zatrzymujemy, bo trzyma wersy POPRZEDNIEGO utworu, ale
  // pamiętamy, że było włączone: w setliście grający przechodzi do
  // następnej piosenki i śledzenie ma iść dalej bez dotykania telefonu.
  const sluchalo = sluchaAutomat;
  if (zatrzymajSluchanie) {
    zatrzymajSluchanie();
    zatrzymajSluchanie = null;
  }
  sluchaAutomat = false;
  zatrzymajMetronomJesliAktywny();

  const app = el<HTMLDivElement>('#app');
  app.replaceChildren();

  // Tryb koncertowy (setlista wybrana, nie "wszystkie"): pasek postepu NAD
  // #tresc, zeby gitarzysta widzial gdzie jest w secie bez otwierania menu.
  const tresc = document.createElement('div');
  tresc.id = 'tresc';
  app.appendChild(tresc);

  // Pasek podglądu technicznego powstaje PRZED dopasowaniem, choć tekst
  // dostaje po. Inaczej dokładałby się do układu już po pomiarze, kurczył
  // kontener i skrót przelewałby się dokładnie o jego wysokość: zmierzone
  // 16 px nadmiaru na `nie-placz-ewka` przy włączonym `?diag=1`.
  const diag = DIAGNOSTYKA ? document.createElement('div') : null;
  if (diag) { diag.className = 'zar-diag'; diag.textContent = ' '; app.appendChild(diag); }

  // Dok sterowania: WŁASNY wiersz układu pod `#tresc`, nie przyciski `fixed`
  // nad treścią. Przyciski przyklejone do rogów zasłaniały ostatnią sekcję
  // i mapę układu (zrzut 402x874, 24.09.2026), czyli kasowały dokładnie tę
  // informację, którą dopasowanie mozolnie zmieściło na ekranie. Jako element
  // przepływu dok odbiera `#tresc` swoją wysokość PRZED pomiarem, więc
  // dopasowanie liczy się już na polu, które naprawdę jest widoczne.
  const dok = document.createElement('div');
  dok.className = 'og-dok';
  // Puste miejsce doku nie jest strefą ekranu gry: bez tego dotknięcie obok
  // przycisku przełączałoby utwór albo wracało do menu.
  dok.addEventListener('click', (zdarzenie) => zdarzenie.stopPropagation());
  const dokLewo = document.createElement('div');
  dokLewo.className = 'og-dok-grupa';
  const dokSrodek = document.createElement('div');
  dokSrodek.className = 'og-dok-srodek';
  if (wybranaSetlistaId !== null) {
    // Tryb koncertowy: pozycja w secie mieszka w doku, a nie w rogu nad
    // akordami, gdzie wchodziła na wiersz metadanych.
    const postep = document.createElement('div');
    postep.className = 'og-postep';
    postep.innerHTML = `<b>${indeks + 1}</b><span>/</span>${UTWORY.length}`;
    dokSrodek.appendChild(postep);
  } else {
    // Tytuł w doku, nie nad akordami: dok i tak ma stałą wysokość, więc
    // napis nie odbiera skrótowi ani piksela z jednego ekranu.
    const tytul = document.createElement('div');
    tytul.className = 'og-dok-tytul';
    const nazwa = document.createElement('b');
    nazwa.textContent = utwor.tytul;
    tytul.appendChild(nazwa);
    if (utwor.wykonawca) {
      const kto = document.createElement('span');
      kto.textContent = utwor.wykonawca;
      tytul.appendChild(kto);
    }
    dokSrodek.appendChild(tytul);
  }
  const dokPrawo = document.createElement('div');
  dokPrawo.className = 'og-dok-grupa';
  dok.append(dokLewo, dokSrodek, dokPrawo);
  app.appendChild(dok);
  // Na wąskim telefonie między pięcioma przyciskami zostaje około 120 px, więc
  // długi tytuł ucinał się po dwóch słowach. Wtedy tytuł dostaje dwa wiersze
  // zamiast wykonawcy: tytuł mówi, co gramy, wykonawca jest dodatkiem.
  // Pomiar w następnej klatce, bo przyciski doku dochodzą niżej w tej funkcji.
  requestAnimationFrame(() => {
    const nazwaWDoku = dok.querySelector<HTMLElement>('.og-dok-tytul b');
    if (nazwaWDoku && nazwaWDoku.scrollWidth > nazwaWDoku.clientWidth + 1) {
      nazwaWDoku.parentElement?.classList.add('og-dok-tytul--dlugi');
    }
  });

  // Dopasowanie DOPIERO po wstawieniu doku: dok ma stałą wysokość z CSS, więc
  // pomiar od razu widzi pole pomniejszone o niego.
  renderujTrescUtworu(utwor, tresc, diag);

  // Przycisk przelacznika widoku, dolny lewy rog - symetrycznie do mikrofonu.
  // `stopPropagation` z tego samego powodu co wszedzie indziej w widoku
  // grania - bez niego dotkniecie dobija babelkowaniem do obslugi calego
  // ekranu i trafia w srodkowa strefe (powrot do menu) albo w brzeg
  // (przelaczenie utworu).
  const tryb = document.createElement('button');
  tryb.type = 'button';
  tryb.className = `og-tryb${trybPelnegoTekstu ? ' og-tryb--aktywny' : ''}`;
  tryb.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="10" x2="17" y2="10"/><line x1="3" y1="14" x2="21" y2="14"/><line x1="3" y1="18" x2="14" y2="18"/></svg>';
  tryb.setAttribute('aria-label', trybPelnegoTekstu ? 'pokaz skrot jednoekranowy' : 'pokaz pelny tekst');
  tryb.addEventListener('click', (zdarzenie) => {
    zdarzenie.stopPropagation();
    trybPelnegoTekstu = !trybPelnegoTekstu;
    tryb.classList.toggle('og-tryb--aktywny', trybPelnegoTekstu);
    tryb.setAttribute('aria-label', trybPelnegoTekstu ? 'pokaz skrot jednoekranowy' : 'pokaz pelny tekst');
    renderujTrescUtworu(utwor, tresc, diag);
  });
  dokLewo.appendChild(tryb);

  // Metronom, srodek dolnej krawedzi - miedzy przelacznikiem widoku (lewo) i
  // mikrofonem (prawo). Samo dotkniecie NIE startuje tykania - otwiera panel
  // (BPM, odliczanie, start/stop), tak jak wiersz metadanych otwiera panel
  // stroju. `stopPropagation` z tego samego powodu co wszedzie indziej tutaj.
  const metro = document.createElement('button');
  metro.type = 'button';
  metro.className = `og-metro${metronomAktywny ? ' og-metro--aktywny' : ''}`;
  metro.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3 5 21h14L12 3Z"/><path d="M9.5 15h5"/><path d="M12 3v9"/></svg>';
  metro.setAttribute('aria-label', 'metronom');
  metro.addEventListener('click', (zdarzenie) => {
    zdarzenie.stopPropagation();
    otworzPanelMetronomu(metro);
  });
  dokLewo.appendChild(metro);

  // Chwyty i stroik (diagramy z chords-db, detekcja wysokości z PitchDetect).
  // Oba otwierają arkusz; żaden nie zmienia stanu utworu.
  const chwyty = document.createElement('button');
  chwyty.type = 'button';
  chwyty.className = 'og-tryb og-chwyty-btn';
  chwyty.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M6 4v16M10 4v16M14 4v16M18 4v16M4 4h16M4 9h16M4 14h16"/><circle cx="10" cy="11.5" r="1.6" fill="currentColor"/><circle cx="14" cy="16.5" r="1.6" fill="currentColor"/></svg>';
  chwyty.setAttribute('aria-label', 'chwyty w utworze');
  chwyty.addEventListener('click', (zdarzenie) => {
    zdarzenie.stopPropagation();
    otworzPanelChwytow(akordyNaEkranie(utwor));
  });

  const stroik = document.createElement('button');
  stroik.type = 'button';
  stroik.className = 'og-tryb og-stroik-btn';
  stroik.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M4 16a8 8 0 0 1 16 0"/><path d="M12 16l3.5-6"/><circle cx="12" cy="16" r="1.4" fill="currentColor"/></svg>';
  stroik.setAttribute('aria-label', 'stroik');
  stroik.addEventListener('click', (zdarzenie) => {
    zdarzenie.stopPropagation();
    otworzStroik();
  });
  dokPrawo.append(chwyty, stroik);

  // Faza 2: przycisk mikrofonu, dolny prawy rog. `stopPropagation` z tego
  // samego powodu co wyzej.
  const mic = document.createElement('button');
  mic.type = 'button';
  mic.className = 'og-mic';
  mic.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="22"/></svg>';
  mic.setAttribute('aria-label', 'sledz spiew');
  const wlaczSledzenieTekstu = (): void => {
    // Śledzenie tekstu ma sens tylko tam, gdzie tekst widać: włączenie
    // mikrofonu przełącza na widok pełnego tekstu.
    if (!sluchaAutomat && !trybPelnegoTekstu) {
      trybPelnegoTekstu = true;
      tryb.classList.add('og-tryb--aktywny');
      tryb.setAttribute('aria-label', 'pokaz skrot jednoekranowy');
      renderujTrescUtworu(utwor, tresc, diag);
    }
    void przelaczSluchanie(mic);
  };
  mic.addEventListener('click', (zdarzenie) => {
    zdarzenie.stopPropagation();
    wlaczSledzenieTekstu();
  });
  dokPrawo.appendChild(mic);
  if (sluchalo) wlaczSledzenieTekstu();
}

function przesun(o: number): void {
  // Setlista z JEDNYM utworem (etap 8): modulo dałoby ten sam indeks, ale
  // specyfikacja żąda czegoś mocniejszego niż "ten sam wynik" - brzeg ekranu
  // ma NIC NIE ROBIĆ, a nie przerysować od nowa TEN SAM utwór. Różnica jest
  // widoczna: `pokazUtwor()` niszczy `#tresc` i liczy `dopasuj()` od zera,
  // czyli mignięcie na telefonie przy każdym dotknięciu brzegu.
  if (UTWORY.length <= 1) return;

  // Tryb koncertowy: koniec setlisty NIE zawija sie na poczatek (specyfikacja
  // "performance mode", zeby gitarzysta nie zgubil sie mysleic, ze gra dalej
  // ten sam set od nowa). Dotyczy tylko ruchu "do przodu" - cofniecie z
  // pierwszego utworu zostaje bez zmian.
  if (wybranaSetlistaId !== null && o > 0 && indeks === UTWORY.length - 1) {
    pokazKoniecSetlisty();
    return;
  }

  indeks = (indeks + o + UTWORY.length) % UTWORY.length;
  pokazUtwor();
}

/** Nakladka "koniec setlisty" (tryb koncertowy): widoczna 1.5s, potem znika
 * z krotkim fade-outem. Nie zmienia `indeks` - utwor zostaje ten sam. */
function pokazKoniecSetlisty(): void {
  const app = el<HTMLDivElement>('#app');
  if (app.querySelector('.og-koniec-overlay')) return;

  const overlay = document.createElement('div');
  overlay.className = 'og-koniec-overlay';
  overlay.addEventListener('click', (zdarzenie) => zdarzenie.stopPropagation());

  const tekst = document.createElement('div');
  tekst.className = 'og-koniec-tekst';
  tekst.textContent = 'koniec setlisty';
  overlay.appendChild(tekst);
  app.appendChild(overlay);

  setTimeout(() => {
    overlay.style.opacity = '0';
    setTimeout(() => overlay.remove(), 300);
  }, 1500);
}

/** Ochrona przed przypadkowym wyjsciem (tryb koncertowy): srodkowe dotkniecie
 * pyta zamiast od razu wracac do menu. Bez odpowiedzi w 3s dymek znika sam i
 * granie trwa dalej. */
function pokazPotwierdzenieWyjscia(): void {
  const app = el<HTMLDivElement>('#app');
  if (app.querySelector('.og-potwierdzenie')) return;

  const box = document.createElement('div');
  box.className = 'og-potwierdzenie';
  box.addEventListener('click', (zdarzenie) => zdarzenie.stopPropagation());

  const tekst = document.createElement('span');
  tekst.textContent = 'wyjsc z grania?';
  box.appendChild(tekst);

  let zamkniete = false;
  const timer = setTimeout(() => zamknij(), 3000);
  function zamknij(): void {
    if (zamkniete) return;
    zamkniete = true;
    clearTimeout(timer);
    box.remove();
  }

  const tak = document.createElement('button');
  tak.type = 'button';
  tak.textContent = 'tak';
  tak.addEventListener('click', (zdarzenie) => {
    zdarzenie.stopPropagation();
    zamknij();
    pokazMenu();
  });
  box.appendChild(tak);

  const nie = document.createElement('button');
  nie.type = 'button';
  nie.textContent = 'nie';
  nie.addEventListener('click', (zdarzenie) => {
    zdarzenie.stopPropagation();
    zamknij();
  });
  box.appendChild(nie);

  app.appendChild(box);
}

/**
 * Reczne sledzenie pozycji (Faza 1): dotkniecie lewego (`kierunek < 0`) albo
 * prawego (`kierunek > 0`) brzegu ekranu w widoku grania. Pierwsze dotkniecie
 * KTOREGOKOLWIEK brzegu aktywuje podswietlenie na sekcji 0 (spec: "pierwsze
 * dotkniecie brzegu aktywuje na sekcji 0"), kolejne przesuwaja wg `pozycja.ts`.
 * Bez sekcji (brak `.og-sec` w renderze, np. utwor niepoprawny) nie robi nic.
 */
function dotkniecieBrzegu(kierunek: -1 | 1): void {
  const tresc = document.getElementById('tresc');
  if (!tresc) return;
  const liczbaSekcji = tresc.querySelectorAll('.og-sec').length;
  if (liczbaSekcji === 0) return;

  // Faza 2: reczne dotkniecie brzegu nadpisuje automat na 5 sekund, zeby
  // gitarzysta poprawiajacy pozycje reka nie zobaczyl, jak automat zaraz
  // odwraca jego decyzje na podstawie tego samego akordu.
  recznaIngerencjaDo = Date.now() + CZAS_NADPISANIA_MS;

  if (aktywnaSekcja === null) {
    aktywnaSekcja = 0;
  } else {
    const stan = { indeks: aktywnaSekcja, liczbaSekcji };
    aktywnaSekcja = kierunek > 0 ? nastepna(stan) : poprzednia(stan);
  }
  podswietlSekcje(tresc, aktywnaSekcja);
}

// -------------------------------------------------------------- widok menu

/** Odświeża `UTWORY` z bazy. W trybie tylko do odczytu nie ma czego
 * odświeżać (nie ma bazy), więc jest to jedyna gałąź warunkowa świadomie
 * pozostawiona bez `try/catch`: skoro tryb tylko do odczytu już się ustalił,
 * kolejne odczyty tej samej bazy nie mają prawa nagle się udać. */
async function odswiezUtwory(): Promise<void> {
  if (trybTylkoDoOdczytu) return;
  WSZYSTKIE_UTWORY = await listaUtworow();
  SETLISTY = await listaSetlist();
  UTWORY = obliczWidoczneUtwory();
}

/** Ikony pozycji listy jako SVG w kolorze tekstu (`currentColor`), zamiast
 * znaków ☆ i ✎, które każda czcionka rysuje inaczej i w innej wielkości. */
const IKONA_GWIAZDKA = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" aria-hidden="true">'
  + '<path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8-4.3-4.1 5.9-.9z"/></svg>';
const IKONA_OLOWEK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
  + '<path d="M4 20h4L19 9l-4-4L4 16v4z"/><path d="M13.5 6.5l4 4"/></svg>';

function pokazMenu(): void {
  zatrzymajMetronomJesliAktywny();

  const app = el<HTMLDivElement>('#app');
  app.dataset.widok = 'menu';
  app.replaceChildren();

  const naglowek = document.createElement('header');
  naglowek.className = 'zar-naglowek';
  naglowek.innerHTML = '<h1>Ognisko</h1><p>Akordy na jeden ekran, bez przewijania</p>';
  app.appendChild(naglowek);

  // Pasek wyboru setlisty (etap 8): bez bazy nie ma czego wybierać ani gdzie
  // zapisać nową, więc w trybie tylko do odczytu ten pasek po prostu nie
  // istnieje - ten sam powód, dla którego stopka niżej chowa `+ dodaj utwór`.
  if (!trybTylkoDoOdczytu) {
    const pasek = document.createElement('nav');
    pasek.className = 'zar-setlisty-pasek';

    const pozycjaWszystkie = document.createElement('button');
    pozycjaWszystkie.type = 'button';
    pozycjaWszystkie.className = `zar-setlisty-pozycja${wybranaSetlistaId === null ? ' zar-setlisty-pozycja--aktywna' : ''}`;
    pozycjaWszystkie.textContent = 'wszystkie';
    pozycjaWszystkie.addEventListener('click', (zdarzenie) => {
      // `stopPropagation`, ten sam powód co wszędzie indziej w tym menu: bez
      // niego kliknięcie dobija bąbelkowaniem do obsługi ekranu gry.
      zdarzenie.stopPropagation();
      if (wybranaSetlistaId === null) return;
      wybranaSetlistaId = null;
      zapiszWybranaSetliste(null);
      UTWORY = obliczWidoczneUtwory();
      pokazMenu();
    });
    pasek.appendChild(pozycjaWszystkie);

    SETLISTY.forEach((setlista) => {
      const aktywna = wybranaSetlistaId === setlista.id;
      const pozycja = document.createElement('button');
      pozycja.type = 'button';
      pozycja.className = `zar-setlisty-pozycja${aktywna ? ' zar-setlisty-pozycja--aktywna' : ''}`;
      pozycja.textContent = setlista.nazwa || '(bez nazwy)';
      pozycja.addEventListener('click', (zdarzenie) => {
        zdarzenie.stopPropagation();
        // Dotknięcie JUŻ WYBRANEJ setlisty drugi raz otwiera jej edycję -
        // wymaganie specyfikacji ("Wejście: `+ setlista` albo dotknięcie już
        // wybranej setlisty drugi raz").
        if (aktywna) {
          pokazEkranSetlisty(setlista);
          return;
        }
        wybranaSetlistaId = setlista.id;
        zapiszWybranaSetliste(setlista.id);
        UTWORY = obliczWidoczneUtwory();
        pokazMenu();
      });
      pasek.appendChild(pozycja);
    });

    const dodajSetliste = document.createElement('button');
    dodajSetliste.type = 'button';
    dodajSetliste.className = 'zar-setlisty-pozycja zar-setlisty-pozycja--dodaj';
    dodajSetliste.textContent = '+ setlista';
    dodajSetliste.addEventListener('click', (zdarzenie) => {
      zdarzenie.stopPropagation();
      pokazEkranSetlisty(null);
    });
    pasek.appendChild(dodajSetliste);

    app.appendChild(pasek);
  }

  // Pole szukania + rząd chipów filtrów (etap 9/10), NAD listą, POD paskiem
  // setlisty. Pole szukania jest wspólny element DOM przez cały czas życia
  // tego widoku menu - re-render przy wpisywaniu odświeża TYLKO `lista`
  // (przez `renderujListe()` niżej), nigdy całe `pokazMenu()`, inaczej pole
  // traciłoby fokus i pozycję kursora po każdym wciśniętym znaku.
  const szukaj = document.createElement('input');
  szukaj.type = 'search';
  szukaj.className = 'zar-szukaj';
  szukaj.placeholder = 'szukaj...';
  szukaj.autocomplete = 'off';
  szukaj.value = filtrSzukaj;
  szukaj.addEventListener('input', () => {
    filtrSzukaj = szukaj.value;
    renderujListe();
  });
  app.appendChild(szukaj);

  // Tagi liczone z `UTWORY` (zakres aktualnie wybranej setlisty, albo
  // wszystkiego), nie z `WSZYSTKIE_UTWORY` - chip pokazujący tag, którego w
  // bieżącym widoku nie ma, byłby zawsze pusty po kliknięciu.
  const dostepneTagi = zbierzTagi(UTWORY);
  // Zapamiętany wybór tagu potrafi wskazywać na tag, którego w NOWYM zakresie
  // (inna setlista) już nie ma - ten sam wzorzec obrony co
  // `obliczWidoczneUtwory` przy skasowanej setlistcie: cichy powrót do
  // "brak filtra", zamiast pustej listy bez wyjaśnienia.
  if (filtrTag !== null && !dostepneTagi.includes(filtrTag)) filtrTag = null;

  const filtry = document.createElement('div');
  filtry.className = 'zar-filtry';

  function renderujFiltry(): void {
    filtry.replaceChildren();

    const chipUlubione = document.createElement('button');
    chipUlubione.type = 'button';
    chipUlubione.className = `zar-filtr${filtrTylkoUlubione ? ' zar-filtr--aktywny' : ''}`;
    chipUlubione.textContent = '★ ulubione';
    chipUlubione.addEventListener('click', (zdarzenie) => {
      zdarzenie.stopPropagation();
      filtrTylkoUlubione = !filtrTylkoUlubione;
      renderujFiltry();
      renderujListe();
    });
    filtry.appendChild(chipUlubione);

    dostepneTagi.forEach((tag) => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = `zar-filtr${filtrTag === tag ? ' zar-filtr--aktywny' : ''}`;
      chip.textContent = tag;
      chip.addEventListener('click', (zdarzenie) => {
        zdarzenie.stopPropagation();
        // Dotknięcie już wybranego tagu drugi raz go WYŁĄCZA - ten sam
        // wzorzec przełącznika co "ulubione" wyżej, tag to nie radio-button.
        filtrTag = filtrTag === tag ? null : tag;
        renderujFiltry();
        renderujListe();
      });
      filtry.appendChild(chip);
    });
  }
  renderujFiltry();
  app.appendChild(filtry);

  const lista = document.createElement('nav');
  lista.className = 'zar-lista';

  function renderujListe(): void {
    lista.replaceChildren();
    const widoczne = obliczListeMenu();
    widoczne.forEach((utwor, i) => {
      // Zwykły `<div>`, nie `<button>`: musi pomieścić DWA niezależne cele
      // kliknięcia (edycja, gwiazdka), a zagnieżdżanie `<button>` w
      // `<button>` jest nieprawidłowym znacznikiem i psuje zdarzenia w
      // części przeglądarek.
      const poz = document.createElement('div');
      poz.className = 'zar-poz';
      poz.setAttribute('role', 'button');
      poz.tabIndex = 0;
      poz.style.setProperty('--i', String(i));
      poz.innerHTML = `<span class="zar-nr">${String(i + 1).padStart(2, '0')}</span>`
        + `<span class="zar-tyt"></span>`;
      const tytEl = poz.querySelector('.zar-tyt') as HTMLElement;
      const nazwa = document.createElement('span');
      nazwa.className = 'zar-tyt-nazwa';
      nazwa.textContent = utwor.tytul;
      tytEl.appendChild(nazwa);
      if (utwor.wykonawca) {
        const art = document.createElement('span');
        art.className = 'zar-artysta';
        art.textContent = utwor.wykonawca;
        tytEl.appendChild(art);
      }

      const otworz = (zdarzenie: Event) => {
        // `stopPropagation` jest tu warunkiem działania, nie ostrożnością:
        // bez niego to samo kliknięcie przełącza widok na granie, a chwilę
        // później dobija bąbelkowaniem do obsługi ekranu gry, która widzi
        // już widok „granie", trafia w środkową strefę i natychmiast wraca
        // do menu.
        zdarzenie.stopPropagation();
        if (!ekranProbowany) void trzymajEkran();
        // `indeks` wskazuje pozycję w `UTWORY` (pełna, NIEFILTROWANA lista w
        // kolejności setlisty - patrz `przesun`/`pokazUtwor`), a `i` tutaj to
        // pozycja w liście PRZEFILTROWANEJ szukaniem/tagiem/ulubionymi - te
        // dwie liczby rozjeżdżają się, gdy filtr cokolwiek ukrywa, więc trzeba
        // odszukać prawdziwą pozycję po referencji utworu.
        indeks = UTWORY.indexOf(utwor);
        pokazUtwor();
      };
      poz.addEventListener('click', otworz);
      poz.addEventListener('keydown', (zdarzenie) => {
        if (zdarzenie.key === 'Enter' || zdarzenie.key === ' ') { zdarzenie.preventDefault(); otworz(zdarzenie); }
      });

      if (!trybTylkoDoOdczytu) {
        const gwiazdka = document.createElement('button');
        gwiazdka.className = `zar-gwiazdka${utwor.ulubiony ? ' zar-gwiazdka--aktywna' : ''}`;
        gwiazdka.type = 'button';
        gwiazdka.innerHTML = IKONA_GWIAZDKA;
        gwiazdka.setAttribute(
          'aria-label',
          utwor.ulubiony ? `usuń ${utwor.tytul} z ulubionych` : `dodaj ${utwor.tytul} do ulubionych`,
        );
        gwiazdka.addEventListener('click', (zdarzenie) => {
          // Ten sam powód co przy `otworz`: bez `stopPropagation` kliknięcie
          // dobija bąbelkowaniem do obsługi ekranu gry, zamiast tylko
          // przełączyć ulubiony status tej pozycji.
          zdarzenie.stopPropagation();
          utwor.ulubiony = !utwor.ulubiony;
          void zapiszUtwor(utwor);
          renderujListe();
        });
        poz.appendChild(gwiazdka);

        const edytuj = document.createElement('button');
        edytuj.className = 'zar-edytuj';
        edytuj.type = 'button';
        edytuj.innerHTML = IKONA_OLOWEK;
        edytuj.setAttribute('aria-label', `edytuj utwór ${utwor.tytul}`);
        edytuj.addEventListener('click', (zdarzenie) => {
          // Ten sam powód co przy `otworz`: bez `stopPropagation` kliknięcie
          // dobija bąbelkowaniem do obsługi ekranu gry.
          zdarzenie.stopPropagation();
          pokazWklejanie(utwor);
        });
        poz.appendChild(edytuj);
      }

      lista.appendChild(poz);
    });
  }
  renderujListe();
  app.appendChild(lista);

  const stopka = document.createElement('footer');
  stopka.className = 'zar-stopka';
  stopka.innerHTML = '<span id="wakelock"></span>'
    + '<span id="offline"></span>'
    + '<span id="trwalosc"></span>'
    + (trybTylkoDoOdczytu
      ? `<span>tryb tylko do odczytu: ${powodTylkoDoOdczytu ?? 'baza danych niedostępna'}</span>`
      : '');
  app.appendChild(stopka);
  odswiezStanEkranu();
  odswiezStanOffline();
  odswiezStanTrwalosci();

  if (!trybTylkoDoOdczytu) {
    const pasek = document.createElement('div');
    pasek.className = 'zar-pasek-akcji';
    // Jedna akcja główna na wierzchu, reszta za „więcej". Pięć równorzędnych
    // przycisków w dwóch rzędach zabierało sześćdziesiąt kilka pikseli listy,
    // a cztery z nich (plik, klucz) używa się raz na miesiąc.
    pasek.innerHTML =
        '<div class="zar-pasek-wiecej" hidden>'
      + '<button type="button" data-akcja="eksport">eksportuj</button>'
      + '<button type="button" data-akcja="import">zastąp</button>'
      + '<button type="button" data-akcja="dolacz">dołącz</button>'
      + '<button type="button" data-akcja="klucz-api">klucz API</button>'
      + '</div>'
      + '<div class="zar-pasek-glowny">'
      + '<button type="button" class="zar-pasek-dodaj" data-akcja="dodaj"><span aria-hidden="true">+</span> dodaj utwór</button>'
      + '<button type="button" class="zar-pasek-przelacz" data-akcja="wiecej" aria-expanded="false" aria-label="więcej akcji">'
      + '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="19" cy="12" r="1.8"/></svg>'
      + '</button></div>';
    app.appendChild(pasek);

    const wiecej = pasek.querySelector<HTMLDivElement>('.zar-pasek-wiecej');
    const przelacz = pasek.querySelector<HTMLButtonElement>('[data-akcja="wiecej"]');
    przelacz?.addEventListener('click', (zdarzenie) => {
      zdarzenie.stopPropagation();
      if (!wiecej) return;
      wiecej.hidden = !wiecej.hidden;
      przelacz.setAttribute('aria-expanded', String(!wiecej.hidden));
    });

    pasek.querySelector('[data-akcja="dodaj"]')?.addEventListener('click', (zdarzenie) => {
      zdarzenie.stopPropagation();
      pokazWklejanie(null);
    });
    pasek.querySelector('[data-akcja="eksport"]')?.addEventListener('click', (zdarzenie) => {
      zdarzenie.stopPropagation();
      void eksportujDoPliku();
    });
    pasek.querySelector('[data-akcja="import"]')?.addEventListener('click', (zdarzenie) => {
      zdarzenie.stopPropagation();
      uruchomImport();
    });
    pasek.querySelector('[data-akcja="dolacz"]')?.addEventListener('click', (zdarzenie) => {
      zdarzenie.stopPropagation();
      uruchomDolaczenie();
    });
    pasek.querySelector('[data-akcja="klucz-api"]')?.addEventListener('click', (zdarzenie) => {
      zdarzenie.stopPropagation();
      ustawKluczElevenLabs();
    });
  }
}

/** Faza 3: proste okno `prompt()` na klucz API ElevenLabs (warstwa
 * najlepszej jakosci STT), zapisywany w `localStorage`, nigdy w kodzie ani
 * repozytorium. Puste albo odwolane okno nie zmienia zapisanego klucza -
 * dopiero wpisanie SAMYCH spacji albo wyczyszczenie pola je usuwa, zeby dalo
 * sie wrocic do samego Whishpera bez konieczności znajomosci starego klucza. */
function ustawKluczElevenLabs(): void {
  let aktualny = '';
  try {
    aktualny = localStorage.getItem(KLUCZ_ELEVENLABS) ?? '';
  } catch {
    // Brak localStorage - proba zapisu nizej i tak zawiedzie po cichu.
  }
  const wpisany = prompt('Klucz API ElevenLabs (rozpoznawanie spiewu, zostaw pusty aby usunac):', aktualny);
  if (wpisany === null) return; // Anulowano - nic sie nie zmienia.

  try {
    if (wpisany.trim() === '') localStorage.removeItem(KLUCZ_ELEVENLABS);
    else localStorage.setItem(KLUCZ_ELEVENLABS, wpisany.trim());
  } catch {
    alert('Nie udalo sie zapisac klucza (localStorage niedostepny).');
  }
}

// ------------------------------------------------------- widok wklejania

/** Wejście do ekranu wklejenia/edycji. `utworDoEdycji === null` = nowy
 * utwór. Po zapisie albo usunięciu wraca do menu ze świeżo odczytaną listą,
 * bo to jedyny sposób, żeby lista na ekranie nie kłamała o stanie bazy. */
function pokazWklejanie(utworDoEdycji: Utwor | null): void {
  const app = el<HTMLDivElement>('#app');
  app.dataset.widok = 'wklejanie';
  app.replaceChildren();

  mountujWklejanie(app, {
    utwor: utworDoEdycji,
    naZapisz: async (utwor) => {
      try {
        await zapiszUtwor(utwor);
      } catch (e) {
        alert(e instanceof BladMagazynu ? e.message : 'Zapis się nie powiódł.');
        return;
      }
      await odswiezUtwory();
      pokazMenu();
    },
    naUsun: async (id) => {
      try {
        await usunUtwor(id);
      } catch (e) {
        alert(e instanceof BladMagazynu ? e.message : 'Usunięcie się nie powiodło.');
        return;
      }
      await odswiezUtwory();
      pokazMenu();
    },
    naPowrot: () => pokazMenu(),
  });
}

// -------------------------------------------------------- widok setlisty

/** Wejście do ekranu przygotowania setlisty. `setlistaDoEdycji === null` =
 * nowa setlista ("+ setlista"). Ekran zapisuje SAM, przy każdej zmianie
 * (patrz `ui/setlista.ts`), więc `naZapisz` tutaj tylko odświeża stan menu -
 * NIE wraca do menu, bo grający zostaje na ekranie setlisty między kolejnymi
 * `▲`/`▼`/`+`, dokładnie jak panel stroju zostaje otwarty między `+`/`−`. */
function pokazEkranSetlisty(setlistaDoEdycji: Setlista | null): void {
  const app = el<HTMLDivElement>('#app');
  app.dataset.widok = 'setlista';
  app.replaceChildren();

  // Setlista utworzona przez "+ setlista" (wejście `null`) staje się aktywna
  // od razu po PIERWSZYM udanym zapisie: grający właśnie ją buduje, więc
  // naturalne jest, że menu i granie od razu jej dotyczą, bez dodatkowego
  // dotknięcia paska. Flaga gaśnie po pierwszym użyciu - kolejne zapisy tej
  // samej sesji edycji (▲/▼/+ /usuń) nie mają czego ponownie "tworzyć".
  let autoWybierzPoZapisie = setlistaDoEdycji === null;

  mountujEkranSetlisty(app, {
    setlista: setlistaDoEdycji,
    wszystkieUtwory: sortujUtwory(WSZYSTKIE_UTWORY),
    naZapisz: async (setlista) => {
      try {
        await zapiszSetliste(setlista);
      } catch (e) {
        alert(e instanceof BladMagazynu ? e.message : 'Zapis setlisty się nie powiódł.');
        return;
      }
      SETLISTY = await listaSetlist();
      if (autoWybierzPoZapisie) {
        autoWybierzPoZapisie = false;
        wybranaSetlistaId = setlista.id;
        zapiszWybranaSetliste(setlista.id);
      }
      if (wybranaSetlistaId === setlista.id) UTWORY = obliczWidoczneUtwory();
    },
    naUsun: async (id) => {
      try {
        await usunSetliste(id);
      } catch (e) {
        alert(e instanceof BladMagazynu ? e.message : 'Usunięcie setlisty się nie powiodło.');
        return;
      }
      // Usunięcie setlisty wraca widok do "wszystkie" - wymaganie
      // specyfikacji - i NIE dotyka magazynu `utwory`.
      if (wybranaSetlistaId === id) {
        wybranaSetlistaId = null;
        zapiszWybranaSetliste(null);
      }
      SETLISTY = await listaSetlist();
      UTWORY = obliczWidoczneUtwory();
      pokazMenu();
    },
    naPowrot: () => pokazMenu(),
  });
}

// ---------------------------------------------------------- eksport/import

function uruchomImport(): void {
  const wejscie = document.createElement('input');
  wejscie.type = 'file';
  wejscie.accept = 'application/json';
  wejscie.addEventListener('change', () => {
    const plik = wejscie.files?.[0];
    if (!plik) return;
    // Powiedziane wprost PRZED wykonaniem: import ZASTĘPUJE zawartość, nie dolewa.
    if (!confirm('Import zastąpi całą obecną zawartość śpiewnika plikiem. Kontynuować?')) return;
    void (async () => {
      try {
        const { liczbaUtworow } = await importujZPliku(plik);
        await odswiezUtwory();
        pokazMenu();
        alert(`Zaimportowano ${liczbaUtworow} utworów.`);
      } catch (e) {
        alert(e instanceof BladMagazynu ? e.message : 'Import się nie powiódł.');
      }
    })();
  });
  wejscie.click();
}

/** Wejście "dołącz" (etap 9, most z Ultimate Guitar): ten sam mechanizm
 * wyboru pliku co `uruchomImport`, ale inne ostrzeżenie PRZED wykonaniem
 * (dołączenie NIE kasuje niczego) i inny czasownik w meldunku końcowym, bo
 * "zaimportowano" sugerowałoby zastąpienie. */
function uruchomDolaczenie(): void {
  const wejscie = document.createElement('input');
  wejscie.type = 'file';
  wejscie.accept = 'application/json';
  wejscie.addEventListener('change', () => {
    const plik = wejscie.files?.[0];
    if (!plik) return;
    if (!confirm(
      'Dołączenie doda z pliku utwory, których jeszcze nie ma w śpiewniku. '
      + 'Utwory już obecne (to samo źródło) zostają bez zmian. Kontynuować?',
    )) return;
    void (async () => {
      try {
        const { dodano, pominieto } = await dolaczZPliku(plik);
        await odswiezUtwory();
        pokazMenu();
        alert(`Dołączono ${dodano} utworów, pominięto ${pominieto} (już były w śpiewniku).`);
      } catch (e) {
        alert(e instanceof BladMagazynu ? e.message : 'Dołączenie się nie powiodło.');
      }
    })();
  });
  wejscie.click();
}

// ---------------------------------------------------------------- stan PWA

/** Rysuje drugi wiersz stopki wg ostatniego `stanPwa`. Wywoływana i przy
 * każdym `pokazMenu()` (bo stopka jest tworzona od zera), i przy każdym
 * meldunku z `zarejestrujWorkera` (bo ten może przyjść, gdy menu już stoi). */
function odswiezStanOffline(): void {
  const cel = document.querySelector<HTMLElement>('#offline');
  if (!cel) return; // menu akurat niewidoczne (widok gry) - nie ma czego rysować

  cel.onclick = null;

  if (stanPwa.rodzaj === 'brak') {
    // Wprost „nie pokazuj wiersza wcale": pusty stan nie jest błędem, tylko
    // brakiem wsparcia albo rejestracją, która się nie udała, a to nie jest
    // coś, co gracz przy ognisku ma czytać.
    cel.style.display = 'none';
    cel.textContent = '';
    return;
  }

  cel.style.display = '';
  if (stanPwa.rodzaj === 'instaluje') {
    cel.textContent = 'przygotowuję do pracy bez sieci';
  } else if (stanPwa.rodzaj === 'gotowe') {
    cel.textContent = 'działa bez sieci';
  } else {
    cel.textContent = 'nowa wersja, dotknij aby uruchomić';
    const zastosuj = stanPwa.zastosuj;
    cel.onclick = (zdarzenie) => {
      // Ten sam powód co przy pozycjach listy utworów: bez `stopPropagation`
      // kliknięcie dobija bąbelkowaniem do obsługi ekranu gry.
      zdarzenie.stopPropagation();
      zastosuj();
    };
  }
}

/** Rysuje wiersz o trwałości danych. Milczy, gdy magazyn jest trwały: to
 * jedyny stan, w którym człowiek nie ma nic do zrobienia, a komunikat
 * „wszystko w porządku" byłby wierszem szumu na ekranie, którego cała
 * obietnica brzmi „tylko to, co potrzebne". */
function odswiezStanTrwalosci(): void {
  const cel = document.querySelector<HTMLElement>('#trwalosc');
  if (!cel) return;
  if (zdanieTrwalosci === null) {
    cel.style.display = 'none';
    cel.textContent = '';
    return;
  }
  cel.style.display = '';
  cel.textContent = zdanieTrwalosci;
}

// ------------------------------------------------------------- blokada ekranu

function odswiezStanEkranu(tekst?: string): void {
  const cel = document.querySelector('#wakelock');
  if (!cel) return;
  if (tekst !== undefined) { cel.textContent = tekst; return; }
  cel.textContent = ekranProbowany
    ? (wakeLock !== null ? 'ekran nie gaśnie' : 'ekran: blokada niedostępna')
    : 'ekran zablokuje się przy pierwszym dotknięciu';
}

/** WakeLock jest wymaganiem numer jeden z MVP i jedynym, którego nie
 * potwierdziłem pierwszorzędnie, więc aplikacja MÓWI WPROST, czy się udało.
 * Cichy brak byłby tu najgorszy: gasnący ekran nie odróżniałby się wtedy od
 * błędu w kodzie. Żądanie musi paść w geście użytkownika. */
async function trzymajEkran(): Promise<void> {
  ekranProbowany = true;
  if (!('wakeLock' in navigator)) {
    odswiezStanEkranu('brak WakeLock, ustaw Autoblokadę na Nigdy');
    return;
  }
  try {
    wakeLock = await navigator.wakeLock.request('screen');
    odswiezStanEkranu('ekran nie gaśnie');
    wakeLock.addEventListener('release', () => {
      wakeLock = null;
      odswiezStanEkranu('blokada ekranu zwolniona');
    });
  } catch (e) {
    odswiezStanEkranu(`WakeLock odmówił (${e instanceof Error ? e.name : 'błąd'})`);
  }
}

// -------------------------------------------------------------------- start

async function start(): Promise<void> {
  ustawWysokosc();

  try {
    await zasiejJesliPusto();
    wybranaSetlistaId = wczytajWybranaSetliste();
    WSZYSTKIE_UTWORY = await listaUtworow();
    const poprawki = poprawkiMetadanych(WSZYSTKIE_UTWORY);
    if (poprawki.length > 0) {
      await zapiszUtworyRazem(poprawki);
      WSZYSTKIE_UTWORY = await listaUtworow();
    }
    SETLISTY = await listaSetlist();
    UTWORY = obliczWidoczneUtwory();
  } catch (e) {
    trybTylkoDoOdczytu = true;
    powodTylkoDoOdczytu = e instanceof BladMagazynu ? e.message : 'baza danych niedostępna';
    UTWORY = sortujUtwory(KORPUS_WBUDOWANY.map(utworZKorpusu));
  }

  pokazMenu();

  // Rejestracja nie może stać na drodze do menu: `zarejestrujWorkera` sama
  // odkłada pracę na `load` i nigdy nie zwraca promise'a, na który dałoby
  // się (przez pomyłkę) zaczekać.
  zarejestrujWorkera((stan) => {
    stanPwa = stan;
    odswiezStanOffline();
  });

  // Pytanie o trwały magazyn nie stoi na drodze do menu i nie ma prawa
  // wywrócić startu: brak tego API zmienia tylko to, co mamy do powiedzenia.
  void sprawdzTrwalosc().then((stan) => {
    zdanieTrwalosci = zdanieOTrwalosci(stan);
    odswiezStanTrwalosci();
  });

  // Nawigacja w widoku gry: skrajne ćwiartki zmieniają utwór, środek wraca do
  // menu. Przy graniu i tak nie ma się tego dotykać, więc strefy są duże i
  // jednoznaczne, zamiast małych celów wymagających patrzenia na ekran.
  el('#app').addEventListener('click', (zdarzenie) => {
    if (!el<HTMLDivElement>('#app').dataset.widok?.startsWith('granie')) return;
    if (!ekranProbowany) { void trzymajEkran(); return; }
    const x = zdarzenie.clientX / window.innerWidth;
    if (x < 0.25) dotkniecieBrzegu(-1);
    else if (x > 0.75) dotkniecieBrzegu(1);
    // Tryb koncertowy (setlista wybrana): srodek pyta zamiast od razu
    // wychodzic, zeby przypadkowe dotkniecie w trakcie grania nie zgubilo
    // pozycji w secie.
    else if (wybranaSetlistaId !== null) pokazPotwierdzenieWyjscia();
    else pokazMenu();
  });

  // Swipe zmienia utwor (przejal role, ktora wczesniej mial dotyk brzegu):
  // `touchstart` zapamietuje punkt startowy, `touchend` liczy przesuniecie i,
  // jesli przekracza 100px, przelacza utwor w kierunku przeciwnym do gestu
  // (przeciagniecie w lewo odslania NASTEPNY utwor, jak w czytniku kart).
  el('#app').addEventListener('touchstart', (zdarzenie) => {
    if (!el<HTMLDivElement>('#app').dataset.widok?.startsWith('granie')) return;
    startX = zdarzenie.touches[0]?.clientX ?? null;
  });
  el('#app').addEventListener('touchend', (zdarzenie) => {
    if (!el<HTMLDivElement>('#app').dataset.widok?.startsWith('granie') || startX === null) return;
    const endX = zdarzenie.changedTouches[0]?.clientX;
    const start = startX;
    startX = null;
    if (endX === undefined) return;
    const dx = endX - start;
    if (Math.abs(dx) <= 100) return;
    przesun(dx < 0 ? 1 : -1);
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && ekranProbowany) void trzymajEkran();
  });

  // Obrót telefonu i schowanie się paska przeglądarki zmieniają dostępną
  // wysokość, więc skrót trzeba dopasować od nowa.
  sledzWysokosc(() => {
    if (el<HTMLDivElement>('#app').dataset.widok?.startsWith('granie')) pokazUtwor();
  });

  // Kolumny pełnego tekstu: po zmianie rozmiaru okna (obrót telefonu,
  // zmiana okna na komputerze) liczba kolumn liczy się od nowa, a kółko myszy
  // przewija kolumny w bok, bo w dół nie ma już czego przewijać.
  window.addEventListener('resize', () => {
    const app = el<HTMLDivElement>('#app');
    const tresc = document.getElementById('tresc');
    if (app.dataset.widok !== 'granie-tekst' || !tresc) return;
    szerokoscKolumny = ulozKolumny(app, tresc);
    const aktywna = tresc.querySelector<HTMLElement>('.pt-grupa--aktywna');
    if (aktywna) przewinDoKolumny(aktywna);
  });
  document.addEventListener('wheel', (z) => {
    const tresc = document.getElementById('tresc');
    if (szerokoscKolumny === null || !tresc || !tresc.contains(z.target as Node)) return;
    if (Math.abs(z.deltaY) <= Math.abs(z.deltaX)) return;
    z.preventDefault();
    tresc.scrollBy({ left: z.deltaY });
  }, { passive: false });

  // Sterowanie klawiaturą/pedałem Bluetooth (patrz `ui/sterowanie.ts`).
  // Nasłuch jeden na cały czas życia aplikacji, nie na każdy render widoku
  // gry - handler sam czyta aktualny `#app[data-widok]` przy każdym
  // naciśnięciu, więc nie musi być podpinany od nowa.
  podepnijSterowanie({
    onNastepnaSekcja: () => {
      const app = el<HTMLDivElement>('#app');
      if (app.dataset.widok === 'granie-tekst') {
        przewinPelnyTekst(1);
      } else {
        dotkniecieBrzegu(1);
      }
    },
    onPoprzedniaSekcja: () => {
      const app = el<HTMLDivElement>('#app');
      if (app.dataset.widok === 'granie-tekst') {
        przewinPelnyTekst(-1);
      } else {
        dotkniecieBrzegu(-1);
      }
    },
    onNastepnyUtwor: () => przesun(1),
    onPoprzedniUtwor: () => przesun(-1),
    onMenu: () => pokazMenu(),
    onMikrofon: () => {
      document.querySelector<HTMLButtonElement>('#app .og-mic')?.click();
    },
  });
}

void start();
